import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../common/SEOHead';
import { getHonestlySEO } from '../../utils/seo';
import { useInView } from '../../hooks/useInView';
import AuthorByline from '../common/AuthorByline';

interface RevealProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

const Reveal: React.FC<RevealProps> = ({ children, delay = 0, className = '' }) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Supply-chain diagram — botanical line-art register, not a corporate flowchart
// Money moves through us. Product bypasses us. That's the whole point.
// ────────────────────────────────────────────────────────────────────────────
const SupplyChainDiagram: React.FC = () => (
  <div className="relative w-full">
    <svg
      viewBox="0 0 800 280"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      aria-hidden="true"
    >
      {/* decorative botanical flourishes */}
      <path d="M40 60 Q60 40 90 50 Q110 58 120 80" stroke="#1B3A2D" strokeWidth="0.8" strokeLinecap="round" opacity="0.25" />
      <path d="M760 60 Q740 40 710 50 Q690 58 680 80" stroke="#1B3A2D" strokeWidth="0.8" strokeLinecap="round" opacity="0.25" />
      <circle cx="40" cy="60" r="2" fill="#1B3A2D" opacity="0.3" />
      <circle cx="760" cy="60" r="2" fill="#1B3A2D" opacity="0.3" />

      {/* node: YOU */}
      <circle cx="120" cy="110" r="46" stroke="#1B3A2D" strokeWidth="1.2" fill="#FBF8F3" />
      <text x="120" y="107" textAnchor="middle" className="fill-forest-800" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '22px', fontStyle: 'italic', fontWeight: 500 }}>
        You
      </text>
      <text x="120" y="128" textAnchor="middle" className="fill-warm-500" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        the buyer
      </text>

      {/* node: LIBERACURE (money only) */}
      <circle cx="400" cy="110" r="56" stroke="#C5A572" strokeWidth="1.4" fill="#FBF8F3" />
      <text x="400" y="104" textAnchor="middle" className="fill-forest-900" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '22px', fontWeight: 600 }}>
        LiberaCure
      </text>
      <text x="400" y="125" textAnchor="middle" className="fill-warm-500" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        the router
      </text>

      {/* node: SUPPLIER */}
      <circle cx="680" cy="110" r="46" stroke="#1B3A2D" strokeWidth="1.2" fill="#FBF8F3" />
      <text x="680" y="107" textAnchor="middle" className="fill-forest-800" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '22px', fontStyle: 'italic', fontWeight: 500 }}>
        Supplier
      </text>
      <text x="680" y="128" textAnchor="middle" className="fill-warm-500" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        15+ years
      </text>

      {/* money path: YOU → LIBERACURE → SUPPLIER (solid forest) */}
      <path d="M168 110 Q230 90 344 110" stroke="#2D5940" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M456 110 Q518 90 632 110" stroke="#2D5940" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M338 110 L344 110 M342 106 L348 110 L342 114" stroke="#2D5940" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <path d="M626 110 L632 110 M630 106 L636 110 L630 114" stroke="#2D5940" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <text x="256" y="82" textAnchor="middle" className="fill-forest-700" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', letterSpacing: '0.08em' }}>
        order + $
      </text>
      <text x="544" y="82" textAnchor="middle" className="fill-forest-700" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', letterSpacing: '0.08em' }}>
        routed + $
      </text>

      {/* product path: SUPPLIER → YOU (dashed gold, bypassing the middle) */}
      <path
        d="M648 142 Q500 240 172 142"
        stroke="#C5A572"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeDasharray="6 5"
        fill="none"
      />
      <path d="M178 142 L172 142 M176 138 L170 142 L176 146" stroke="#C5A572" strokeWidth="1.4" strokeLinecap="round" fill="none" />
      <text x="400" y="238" textAnchor="middle" className="fill-gold-700" style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '16px', fontStyle: 'italic' }}>
        product ships directly — bypasses us entirely
      </text>

      {/* tiny legend */}
      <g transform="translate(40, 260)">
        <line x1="0" y1="0" x2="20" y2="0" stroke="#2D5940" strokeWidth="1.4" />
        <text x="28" y="4" className="fill-warm-600" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', letterSpacing: '0.1em' }}>money flow</text>
        <line x1="130" y1="0" x2="150" y2="0" stroke="#C5A572" strokeWidth="1.4" strokeDasharray="4 3" />
        <text x="158" y="4" className="fill-warm-600" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', letterSpacing: '0.1em' }}>product flow</text>
      </g>
    </svg>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Self Q&A — six honest questions we'd ask ourselves if we were the buyer
// ────────────────────────────────────────────────────────────────────────────
const qa = [
  {
    q: "You're brand new. Why trust you?",
    a: "You shouldn't. Not on day one. Start with a small order — thirty, fifty dollars, see it arrive — then decide if you want to come back. We'd rather you test us than trust us.",
  },
  {
    q: "Why only crypto? That's sketchy.",
    a: "Visa and Mastercard blacklist pharmacy merchant categories like ours. Not because we're doing anything illegal in India — card networks simply don't want the headache of cross-border Rx. We use NOWPayments as the checkout processor: a documented crypto payment gateway, not a random address we DM'd to you out of the blue. Every transaction is on-chain and publicly traceable.",
  },
  {
    q: "How do I know the pills are real?",
    a: "Because we don't pack them. An Indian generic supplier with fifteen-plus years of operation does. Their business dwarfs ours — selling counterfeits would end them overnight. They ship. We route. Our survival is downstream of theirs.",
  },
  {
    q: "What if customs seizes my package?",
    a: "We reship. Twice. For free. If the third attempt also fails, we refund in crypto to the address you paid from. It's written on this page. It's written on the checkout. It's not buried in a PDF.",
  },
  {
    q: "What if you just vanish?",
    a: "Fair. We can't prove a negative. What we can say: we don't hold your product — the supplier does. The payment flows through NOWPayments — a documented crypto processor, not an anonymous wallet — and every transaction is on-chain and publicly traceable. Any order already routed to the supplier ships whether we're around or not. Start small. See what happens.",
  },
  {
    q: "Why not buy from the supplier directly?",
    a: "You can. Seriously. What we add: English support, customs navigation, crypto checkout, the reship guarantee, and best-price routing across multiple suppliers. If none of that is worth the margin, please go direct. We'd rather send you there than oversell.",
  },
  {
    q: "Is this legal where I live?",
    a: "Depends on where you live, and we're not your lawyer. The short version: most Western countries (US, UK, EU, Canada, Australia) have a \u201Cpersonal import\u201D framework that permits small quantities — typically around a 90-day supply — for personal use, not for resale. That's the framework LiberaCure is built around. We put together a country-by-country summary at /policy/import-legality with links to official government guidance — read it before ordering, and if your jurisdiction isn't clearly covered, don't.",
  },
  {
    q: "Will you ship anything if I pay enough?",
    a: "No. Narcotics, opioids, benzodiazepines, antipsychotics, anabolic steroids, and Schedule II–IV controlled substances are off-limits for us — even within the 30 or 90-day personal-import allowances some countries technically permit. Those drugs need a clinician adjusting the dose in real time, and a package in the mail is not a substitute for that. We'd rather lose the order than route a category we can't responsibly serve. The full list lives at /policy/restricted-substances.",
  },
];

// ────────────────────────────────────────────────────────────────────────────
// The promise: three dark forest cards
// ────────────────────────────────────────────────────────────────────────────
const promises = [
  {
    num: '01',
    title: 'Start small.',
    body: "A thirty-dollar first order is less than a single month of almost any subscription. Make us earn it. If it arrives and works, come back for the bigger one. If it doesn't, you risked a coffee and a sandwich.",
  },
  {
    num: '02',
    title: 'Reship twice.',
    body: "If your package doesn't arrive, we reship. If the second attempt doesn't arrive, we reship again. Both free. No forms, no arbitration, no apology theater — just email us and we send another.",
  },
  {
    num: '03',
    title: 'Refund third.',
    body: "If the third attempt still doesn't arrive, we refund in crypto to the wallet you paid from. It's the fallback, not the goal — but it's the fallback that lets the whole structure work.",
  },
];

// ────────────────────────────────────────────────────────────────────────────

const Honestly: React.FC = () => {
  return (
    <div className="bg-cream-50">
      <SEOHead seoData={getHonestlySEO()} />

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[50%] h-[100%] bg-gradient-to-bl from-forest-100/50 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[40%] h-[80%] bg-gradient-to-tr from-gold-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative section-container py-28 sm:py-36 lg:py-44">
          <div className="max-w-4xl">
            <span
              className="text-nano font-semibold text-gold-700 uppercase tracking-[0.25em] mb-8 block animate-fade-up"
              style={{ animationDelay: '0.1s', opacity: 0 }}
            >
              — Honestly —
            </span>

            <h1
              className="font-display text-display-lg sm:text-display-xl lg:text-display-hero text-forest-900 tracking-tight leading-[1.02] mb-10 animate-fade-up italic"
              style={{ animationDelay: '0.2s', opacity: 0 }}
            >
              &ldquo;Yes, this probably looks&nbsp;like&nbsp;a&nbsp;scam.&rdquo;
            </h1>

            <p
              className="text-body-lg sm:text-xl text-warm-600 max-w-2xl leading-relaxed animate-fade-up"
              style={{ animationDelay: '0.3s', opacity: 0 }}
            >
              You&rsquo;d be right to be skeptical. LiberaCure routes orders to
              licensed Indian generic pharmacies — we don&rsquo;t dispense, don&rsquo;t
              warehouse, and won&rsquo;t ship Schedule II–IV controlled substances.
              Crypto-only checkout, personal-import framework. Here&rsquo;s why we
              think the model is worth five minutes &mdash; and what to do if it isn&rsquo;t.
            </p>
          </div>
        </div>
      </section>

      {/* ===== "We don't touch the drugs" — asymmetric grid + diagram ===== */}
      <section className="py-24 sm:py-32 lg:py-40 border-t border-warm-200">
        <div className="section-container">
          <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-20 lg:mb-28">
            <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-6 block">
                The Structure
              </span>
              <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight leading-tight">
                We don&rsquo;t touch the drugs.
              </h2>
              <p className="font-accent italic text-body-lg text-gold-700 mt-6 leading-relaxed">
                Money flows through us. Product never does.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-7">
              <p className="text-body-lg text-warm-600 leading-relaxed">
                LiberaCure is a routing layer, not a pharmacy. When you place an
                order, an Indian generic supplier with fifteen-plus years on the
                ground packs it, labels it, and ships it directly to your door.
                We never warehouse, repack, or handle the product.
              </p>
              <p className="text-body-lg text-warm-600 leading-relaxed">
                What we do is translate between you and them &mdash; English
                customer support, crypto checkout, customs navigation, a reship
                guarantee, and best-price routing across the suppliers we&rsquo;ve
                vetted. And vetting is most of the work, honestly: plenty of
                Indian &ldquo;pharmacies&rdquo; are themselves scams &mdash; fake
                GMP certificates, phantom addresses, operators who disappear
                mid-order. You could hunt for one yourself and hope you pick a real
                one. We already ran the audit so you don&rsquo;t have to.
              </p>
              <p className="text-body-lg text-warm-600 leading-relaxed">
                The structural consequence is simple: for us to run off with
                your pills, we&rsquo;d need to hold them first. We don&rsquo;t.
                If LiberaCure disappears tomorrow, orders already routed to the
                supplier still ship. That&rsquo;s not an accident. It&rsquo;s the
                whole reason this business exists in this shape.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120} className="bg-cream-100 border border-warm-200 rounded-3xl p-8 sm:p-12 lg:p-16">
            <SupplyChainDiagram />
          </Reveal>
        </div>
      </section>

      {/* ===== Self Q&A — bento grid ===== */}
      <section className="bg-cream-100/60 py-24 sm:py-32">
        <div className="section-container">
          <div className="max-w-3xl mb-16">
            <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-4 block">
              The Six Questions
            </span>
            <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight leading-tight">
              What we&rsquo;d ask if we were you.
            </h2>
            <p className="text-body-lg text-warm-600 mt-6 leading-relaxed max-w-2xl">
              No marketing-speak answers. Just the ones we&rsquo;d want if we were
              reading this page on some website we&rsquo;d never heard of.
            </p>
          </div>

          <Reveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl">
            {qa.map((item, idx) => (
              <div
                key={idx}
                className="bg-cream-50 rounded-2xl border border-warm-200 p-8 sm:p-10 hover:shadow-hover hover:-translate-y-0.5 transition-all duration-500 ease-spring"
              >
                <span className="font-accent italic text-caption text-gold-600 mb-4 block">
                  Question {String(idx + 1).padStart(2, '0')}
                </span>
                <h3 className="font-display text-heading-lg text-forest-900 mb-4 tracking-tight leading-snug">
                  &ldquo;{item.q}&rdquo;
                </h3>
                <p className="text-body text-warm-600 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ===== The Promise — dark forest cards ===== */}
      <section className="py-24 sm:py-32">
        <div className="section-container mb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-end">
            <div>
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-4 block">
                The Promise
              </span>
              <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight leading-tight">
                Start small.<br />Reship twice.<br />Refund third.
              </h2>
            </div>
            <p className="text-body text-warm-500 lg:text-right max-w-md lg:ml-auto">
              Every promise on this page is posted in the open — on the product
              page, on the checkout, in the footer. Not in a PDF. Not in
              fine print.
            </p>
          </div>
        </div>

        <div className="section-container">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {promises.map((p) => (
              <div
                key={p.num}
                className="group bg-forest-900 rounded-2xl p-8 sm:p-10 text-cream-100 relative overflow-hidden hover:scale-[1.015] transition-transform duration-500 ease-spring"
              >
                <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-gold-400/5 rounded-full blur-3xl pointer-events-none" />
                <div className="relative">
                  <span className="text-[5rem] sm:text-[6rem] font-display leading-none text-cream-100/5 absolute top-[-20px] right-[-10px] tracking-tighter">
                    {p.num}
                  </span>
                  <h3 className="font-display text-heading-lg text-cream-100 mb-4 tracking-tight italic">
                    {p.title}
                  </h3>
                  <p className="text-body-sm text-warm-400 leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Closing CTA ===== */}
      <section className="border-t border-warm-200 bg-cream-100/50">
        <div className="section-container py-24 sm:py-32">
          <Reveal className="max-w-2xl mx-auto text-center">
            <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight leading-tight italic mb-6">
              &ldquo;Try us small, or don&rsquo;t try us at all.&rdquo;
            </h2>
            <p className="text-body-lg text-warm-600 mb-10 max-w-lg mx-auto leading-relaxed">
              We&rsquo;ve said everything we can say. The rest happens when a
              package does — or doesn&rsquo;t — arrive at your door.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
              <Link to="/shop" className="btn-primary gap-2.5">
                Browse Products
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link to="/inquiry" className="btn-secondary">
                Talk To Us First
              </Link>
            </div>

            <p className="font-accent italic text-body text-warm-500 leading-relaxed max-w-xl mx-auto pt-8 border-t border-warm-200">
              If this page didn&rsquo;t land with you, please don&rsquo;t order.
              We&rsquo;d rather lose a sale than earn distrust.
            </p>

            <div className="max-w-xl mx-auto text-left">
              <AuthorByline />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Honestly;
